import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, BadgeCheck, Package, Scale, X } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";

const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function ProductCard({ p, i, onSelect }) {
  return (
    <motion.article
      layout
      key={p.id}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`product-card-${slug(p.name)}`}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-stone2 bg-white transition-all duration-500 hover:-translate-y-2 hover:border-gold hover:shadow-[0_24px_60px_-18px_rgba(212,139,22,0.35)]"
      onClick={() => onSelect(p)}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={p.image}
          alt={p.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.07]"
        />
        <span
          className={`absolute left-4 top-4 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-cream ${
            p.category === "spices" ? "bg-chilli" : "bg-botanical"
          }`}
        >
          {p.category === "spices" ? "Spice" : "Cold Pressed Oil"}
        </span>
        {!p.available && (
          <span className="absolute right-4 top-4 rounded-full bg-ink/80 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-cream">
            On Request
          </span>
        )}
      </div>
      <div className="flex flex-1 flex-col p-6">
        <h3 className="font-serif text-xl font-semibold text-ink">{p.name}</h3>
        <p className="mt-1.5 text-sm text-pebble">{p.tagline}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(p.category === "oils"
            ? ["Single Cold Pressed", "Batch Traceable"]
            : ["Steam Sterilized", "Lab Tested"]
          ).map((chip) => (
            <span key={chip} className="rounded-full bg-sand px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-botanical">
              {chip}
            </span>
          ))}
        </div>
        <div className="mt-auto flex items-center justify-between pt-5">
          <span className="font-mono text-xs uppercase tracking-[0.16em] text-gold">MOQ · {p.moq}</span>
          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-stone2 text-smoke transition-all duration-300 group-hover:border-gold group-hover:bg-gold group-hover:text-white">
            <ArrowUpRight size={16} />
          </span>
        </div>
      </div>
    </motion.article>
  );
}

function ProductSection({ id, eyebrow, title, blurb, items, dark, onSelect }) {
  return (
    <section id={id} data-testid={`${id}-section`} className={`${dark ? "bg-night" : "bg-cream"} py-20 md:py-28`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal className="max-w-2xl">
          <Eyebrow dark={dark}>{eyebrow}</Eyebrow>
          <h2 className={`mt-3 font-serif text-3xl font-semibold leading-tight sm:text-4xl lg:text-5xl ${dark ? "text-cream" : "text-ink"}`}>
            {title}
          </h2>
          <p className={`mt-5 text-sm leading-relaxed sm:text-base ${dark ? "text-cream/60" : "text-smoke"}`}>{blurb}</p>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8">
          {items.map((p, i) => (
            <ProductCard key={p.id} p={p} i={i} onSelect={onSelect} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Products({ products, onEnquire }) {
  const [selected, setSelected] = useState(null);
  const spices = products.filter((p) => p.category === "spices");
  const oils = products.filter((p) => p.category === "oils");

  return (
    <>
      <ProductSection
        id="products"
        eyebrow="Export Spices"
        title="Spice-belt staples, graded for global buyers"
        blurb="Hand-sorted finger turmeric and sun-dried red chillies — Teja, Sannam, Byadgi and every grade in between — from India's finest growing belts, lab-certified for curcumin, colour and cleanliness."
        items={spices}
        onSelect={setSelected}
      />
      <ProductSection
        id="oils"
        dark
        eyebrow="Tsugiva Own Label · Cold Pressed Oils"
        title="Our own cold pressed oil collection"
        blurb="Groundnut, sunflower, sesame, flaxseed and extra virgin olive — crushed in wooden presses below 45°C, triple-filtered and nitrogen-sealed under the Tsugiva label. Available in bulk and private-label retail formats."
        items={oils}
        onSelect={setSelected}
      />

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="product-spec-modal"
            className="fixed inset-0 z-[70] flex items-end justify-center bg-ink/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              data-lenis-prevent
              className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-3xl bg-white sm:rounded-3xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative h-56 sm:h-64">
                <img src={selected.image} alt={selected.name} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
                <button
                  data-testid="product-modal-close-button"
                  onClick={() => setSelected(null)}
                  className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-ink transition-transform hover:scale-110"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
                <div className="absolute bottom-5 left-6 right-6">
                  <h3 className="font-serif text-2xl font-bold text-cream sm:text-3xl">{selected.name}</h3>
                  <p className="mt-1 text-sm text-cream/80">{selected.tagline}</p>
                </div>
              </div>
              <div className="p-6 sm:p-8">
                <p className="text-sm leading-relaxed text-smoke sm:text-base">{selected.description}</p>
                <div className="mt-7 grid gap-3 sm:grid-cols-2">
                  {selected.specs.map((s) => (
                    <div key={s.key} className="flex items-center justify-between rounded-xl bg-sand px-4 py-3">
                      <span className="text-xs font-medium uppercase tracking-[0.12em] text-pebble">{s.key}</span>
                      <span className="text-sm font-semibold text-ink">{s.value}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-7 flex flex-wrap gap-4 text-sm text-smoke">
                  <span className="flex items-center gap-2">
                    <Scale size={16} className="text-gold" /> MOQ: <b className="text-ink">{selected.moq}</b>
                  </span>
                  <span className="flex items-center gap-2">
                    <Package size={16} className="text-gold" /> {selected.packaging}
                  </span>
                </div>
                <div className="mt-6 flex flex-wrap gap-2" data-testid="product-modal-trust-strip">
                  {[
                    "Batch-coded CoA on request",
                    "SGS / Intertek inspection welcome",
                    selected.category === "oils" ? "Nitrogen sealed" : "Fumigation certified",
                  ].map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-gold/40 bg-gold/5 px-3 py-1.5 text-[11px] font-semibold text-gold">
                      <BadgeCheck size={13} /> {t}
                    </span>
                  ))}
                </div>
                <button
                  data-testid="product-modal-enquire-button"
                  onClick={() => {
                    onEnquire(selected.name);
                    setSelected(null);
                  }}
                  className="mt-8 w-full rounded-full bg-chilli py-3.5 text-sm font-semibold text-cream transition-transform duration-300 hover:scale-[1.02]"
                >
                  Enquire About This Product
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
